/**
 * UI route paths, and the validation for "where was I heading before the login
 * screen bounced me".
 *
 * NOTE: these are BROWSER routes, not API paths. The API still serves
 * `/admin/*` and `/portal/*` — `AuthContext.loginPath` and friends are endpoint
 * URLs and are deliberately unaffected by anything here.
 *
 * The super-admin console sits under `/cp` rather than the guessable `/admin`.
 * That is obscurity, not security — the real boundary is the `aud: sa` JWT — but
 * it keeps the console out of the way of drive-by scanners.
 *
 * WHY THE VALIDATORS EXIST: the tenant portal is mounted at the site root, so a
 * `from` value now looks like `/sales` instead of `/portal/sales`. A naive
 * `from.startsWith('/')` check would accept `//evil.com`, which browsers read as
 * a protocol-relative URL — an open redirect straight off our origin. Both
 * validators match the PATH against known routes and fall back to a safe home.
 */

export const CP_HOME = '/cp';
export const CP_LOGIN = '/cp/login';
export const PORTAL_HOME = '/';
export const PORTAL_LOGIN = '/login';

/**
 * Top-level tenant report sections. Kept in step with the nav in PortalLayout —
 * if one drifts, a deep link simply lands on the dashboard instead of the page
 * asked for, which is a papercut rather than a hole.
 */
const PORTAL_SECTIONS = [
  'sales',
  'sales-returns',
  'sales-history',
  'shifts',
  'ledger',
  'receivables',
  'payables',
  'tax',
  'inventory',
  'quotations',
  'deliveries',
  'orders',
  'purchases',
  'purchase-returns',
] as const;

const PORTAL_PATH = new RegExp(`^/(?:${PORTAL_SECTIONS.join('|')})(?:/[^/].*)?$`);
const CP_PATH = /^\/cp(?:\/[^/].*)?$/;

/**
 * `from` arrives as pathname + search (see AuthGate). Only the pathname decides
 * whether the target is ours; the query string is carried through untouched
 * because the report screens keep their whole filter state in it, and dropping
 * it would silently reset a tenant's date range on every re-login.
 */
function splitPath(from: string): { path: string; rest: string } {
  const cut = from.search(/[?#]/);
  return cut === -1
    ? { path: from, rest: '' }
    : { path: from.slice(0, cut), rest: from.slice(cut) };
}

/** Where an SA lands after signing in: back where they were, or the console. */
export function safeCpRedirect(from: string | undefined): string {
  if (from === undefined || from === '') return CP_HOME;
  const { path, rest } = splitPath(from);
  // Never bounce straight back to the login screen we just came from.
  if (path !== CP_LOGIN && CP_PATH.test(path)) return path + rest;
  return CP_HOME;
}

/** Where a tenant user lands after signing in: back where they were, or home. */
export function safePortalRedirect(from: string | undefined): string {
  if (from === undefined || from === '') return PORTAL_HOME;
  const { path, rest } = splitPath(from);
  if (PORTAL_PATH.test(path)) return path + rest;
  return PORTAL_HOME;
}
